import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MatchInterviewEvent {
	id: string;
	title: string;
	start_time: string;
	end_time: string;
	location?: string | null;
	status?: string | null;
	participantCount: number;
}

export interface MatchInterviewEventsResult {
	upcoming: MatchInterviewEvent[];
	past: MatchInterviewEvent[];
	loading: boolean;
}

export const useMatchInterviewEvents = (matchId: string | undefined): MatchInterviewEventsResult => {
	const [upcoming, setUpcoming] = useState<MatchInterviewEvent[]>([]);
	const [past, setPast] = useState<MatchInterviewEvent[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			if (!matchId) {
				setLoading(false);
				return;
			}
			setLoading(true);

			// JSONB containment: events whose relations.matches array contains the matchId.
			const { data, error } = await supabase
				.from("events")
				.select("id, title, start_time, end_time, location, status, relations")
				.contains("relations", { matches: [matchId] } as Record<string, unknown>)
				.order("start_time", { ascending: false });

			if (cancelled) return;

			if (error) {
				console.error("Error loading match interview events:", error);
				setLoading(false);
				return;
			}

			const now = new Date().toISOString();
			const upcomingList: MatchInterviewEvent[] = [];
			const pastList: MatchInterviewEvent[] = [];

			for (const row of data || []) {
				const event: MatchInterviewEvent = {
					id: row.id,
					title: row.title,
					start_time: row.start_time,
					end_time: row.end_time,
					location: row.location,
					status: row.status,
					participantCount: 0,
				};
				if (event.start_time >= now)
					upcomingList.push(event);
				else
					pastList.push(event);
			}

			// Upcoming: nearest first
			upcomingList.sort((a, b) => a.start_time.localeCompare(b.start_time));
			// Past: most recent first (already desc from query, keep as is)

			setUpcoming(upcomingList);
			setPast(pastList);
			setLoading(false);
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [matchId]);

	return { upcoming, past, loading };
};
